// The Forest's wood-cutting minigame: a timed swing-timing game. A marker sweeps
// a bar; tap CHOP (or Space) when it's over the sweet zone to land a good swing —
// dead-center is a clean chop worth two hits. A few good hits fell a tree, which
// banks a lumber log; then a fresh tree rolls in and the zone tightens. This
// module owns the meter + scoring (DOM overlay, reusing the .hoop-* meter); the
// forest scene animates the axe/chips/tree via the onChop callback.

const DURATION = 30;          // seconds per session
const LOCKOUT = 0.3;          // brief pause after a swing (axe follow-through)
const HITS_PER_TREE = 3;      // good hits to fell a tree (a clean chop counts as 2)

export class Woodcut {
  constructor({ onChop, onFinish } = {}){
    this.onChop = onChop || (() => {});
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    this.timeLeft = DURATION;
    this.chops = 0; this.goodChops = 0; this.cleanChops = 0;
    this.treeHits = 0; this.treesFelled = 0;
    this.streak = 0; this.bestStreak = 0;

    this.phase = 0;
    this.lockout = 0;

    this._onKey = this._onKey.bind(this);
  }

  get speed(){ return Math.min(6.5, 2.8 + this.treesFelled * 0.35); }   // rad/s, ramps per tree
  get zoneW(){ return Math.max(5, 13 - this.treesFelled * 0.9); }       // % half-width, tightens

  mount(root){
    const el = document.createElement('div');
    el.className = 'hoop-hud';                     // reuse the shared meter layout
    el.innerHTML = `
      <div class="hoop-info">
        <span class="pill hoop-timer">0:30</span>
        <span class="pill wood-lumber">🪵 0</span>
        <span class="pill hoop-streak" hidden></span>
      </div>
      <div class="hoop-feedback"></div>
      <div class="hoop-bar">
        <div class="hoop-zone"></div>
        <div class="hoop-marker"></div>
      </div>
      <button class="hoop-shoot wood-chop">🪓 CHOP</button>`;
    (root || document.body).appendChild(el);
    this.el = el;
    this.timerEl = el.querySelector('.hoop-timer');
    this.lumberEl = el.querySelector('.wood-lumber');
    this.streakEl = el.querySelector('.hoop-streak');
    this.feedbackEl = el.querySelector('.hoop-feedback');
    this.zoneEl = el.querySelector('.hoop-zone');
    this.markerEl = el.querySelector('.hoop-marker');

    el.querySelector('.wood-chop').addEventListener('pointerdown', e => { e.preventDefault(); this._chop(); });
    el.querySelector('.hoop-bar').addEventListener('pointerdown', e => { e.preventDefault(); this._chop(); });
    window.addEventListener('keydown', this._onKey);
    this._renderZone();
  }

  begin(){ this.active = true; }

  _onKey(e){
    if (!this.active) return;
    if (e.code === 'Space' || e.code === 'Enter'){ e.preventDefault(); this._chop(); }
  }

  _renderZone(){
    if (!this.zoneEl) return;
    this.zoneEl.style.left = `${50 - this.zoneW}%`;
    this.zoneEl.style.width = `${this.zoneW * 2}%`;
  }

  _chop(){
    if (!this.active || this.lockout > 0) return;
    const pos = 50 + 50 * Math.sin(this.phase);
    const dist = Math.abs(pos - 50);
    this.chops++;
    this.lockout = LOCKOUT;

    const good = dist <= this.zoneW;
    const clean = dist <= this.zoneW * 0.35;
    let felled = false;
    if (good){
      this.goodChops++;
      if (clean) this.cleanChops++;
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
      this.treeHits += clean ? 2 : 1;                 // a clean chop bites deeper
      if (this.treeHits >= HITS_PER_TREE){
        this.treeHits = 0; this.treesFelled++; felled = true;
        this._feedback('TIMBER! 🪵', 'swish');
      } else {
        this._feedback(clean ? 'clean chop!' : 'chop!', clean ? 'swish' : 'made');
      }
    } else {
      this.streak = 0;
      this._feedback('missed the mark', 'miss');
    }
    this._renderZone();
    this._render();
    this.onChop({ good, clean, felled, streak: this.streak });
  }

  update(dt){
    if (!this.active || this.finished) return;

    this.timeLeft -= dt;
    if (this.timeLeft <= 0){ this.timeLeft = 0; this._renderTimer(); return this._finish(); }
    this._renderTimer();

    if (this.lockout > 0){ this.lockout -= dt; return; }
    this.phase += this.speed * dt;
    const pos = 50 + 50 * Math.sin(this.phase);
    this.markerEl.style.left = `${pos}%`;
  }

  _renderTimer(){
    const s = Math.ceil(this.timeLeft);
    this.timerEl.textContent = `0:${String(s).padStart(2, '0')}`;
    this.timerEl.classList.toggle('low', s <= 5);
  }

  _render(){
    this.lumberEl.textContent = `🪵 ${this.treesFelled}`;
    if (this.streak >= 2){
      this.streakEl.hidden = false;
      this.streakEl.textContent = `🔥 ${this.streak} streak`;
      this.streakEl.classList.remove('pulse'); void this.streakEl.offsetWidth; this.streakEl.classList.add('pulse');
    } else {
      this.streakEl.hidden = true;
    }
  }

  _feedback(text, cls){
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = text;
    this.feedbackEl.className = `hoop-feedback show ${cls}`;
    void this.feedbackEl.offsetWidth;
    this.feedbackEl.classList.add('anim');
  }

  _finish(){
    if (this.finished) return;
    this.finished = true;
    this.active = false;
    const lumber = this.treesFelled;
    const crumbs = this.treesFelled + Math.floor(this.bestStreak / 3);
    this.onFinish({
      chops: this.chops, goodChops: this.goodChops, cleanChops: this.cleanChops,
      treesFelled: this.treesFelled, bestStreak: this.bestStreak, lumber, crumbs
    });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    if (this.el) this.el.remove();
    this.el = null;
  }
}
